<script setup>
import { vElementSize } from '@vueuse/components'
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'

const props = defineProps({ blok: Object })

const status = ref()
const getHeight = ref(504)
const getWidth = ref(896)

const hasConsent = useCookie('dialog', {
  sameSite: 'strict',
  default: () => 'no',
  // 0.01 = 10min
  maxAge: 0.0004 * 24 * 60 * 60,
})
const showConsentAlert = ref(false)

// Temporary popup campaign control.
// true = enable this popup campaign, false = disable this popup campaign completely.
const shouldShowPopup = true

// Temporary country restriction.
// true = show only for visitors whose IP country is Thailand.
// false = show worldwide. Change this to false when this temporary restriction should be removed.
const limitPopupToThailand = true

async function isVisitorInThailand() {
  try {
    const response = await fetch('https://get.geojs.io/v1/ip/country.json', {
      cache: 'no-store',
    })
    const data = await response.json()

    return data?.country === 'TH'
  }
  catch (error) {
    console.warn('Cannot detect visitor country:', error)
    return false
  }
}

onMounted(async () => {
  if (process.server)
    return
  if (hasConsent.value === 'yes')
    return
  if (!shouldShowPopup)
    return

  // Temporary condition: keep this enabled to show the popup only in Thailand.
  // Set limitPopupToThailand to false above to show the popup worldwide.
  if (limitPopupToThailand && !(await isVisitorInThailand()))
    return

  showConsentAlert.value = true
})

function acceptConsent() {
  hasConsent.value = 'yes'
  showConsentAlert.value = false
}

const expirationDate = new Date(new Date().getTime() + 1 * 60 * 1000)
const token = useCookie('expired_cookie', {
  default: () => 'no',
  expires: expirationDate,
  watch: true,
})

if (token.value && token.value !== null)
  status.value = 'Cookie successfully set. Expiration in seconds'
else
  status.value = 'Cookie NOT set. Please make sure your browser is accepting cookies'

function onResize({ height, width }) {
  getHeight.value = height
  getWidth.value = width
}
</script>

<template>
  <transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="transform opacity-0"
    enter-to-class="transform opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="transform opacity-100"
    leave-to-class="transform opacity-0"
  >
    <div v-if="showConsentAlert && !blok.disable" :class="showConsentAlert && !blok.disable ? 'opacity-100' : 'opacity-0'" class="fixed inset-0 z-[1000] mx-auto h-full flex items-center justify-center transition-all">
      <div
        :class="showConsentAlert && !blok.disable ? 'opacity-100 -translate-y-2' : 'opacity-0 translate-y-0'"
        :style="{
        }" class="relative m-3 max-w-4xl rounded-md text-white transition-all"
      >
        <!-- <div class="h-ful absolute inset-0 z-1 w-full bg-primary/5" /> -->
        <div class="absolute right-1 top-1 z-10 mr-auto h-8 w-8 cursor-pointer" @click="acceptConsent">
          <svg class="h-full w-full text-white/80 transition-color duration-100 ease-in-out hover:text-white" xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"><path fill="currentColor" d="m12 13.4l-2.9 2.9q-.275.275-.7.275t-.7-.275q-.275-.275-.275-.7t.275-.7l2.9-2.9l-2.9-2.875q-.275-.275-.275-.7t.275-.7q.275-.275.7-.275t.7.275l2.9 2.9l2.875-2.9q.275-.275.7-.275t.7.275q.3.3.3.713t-.3.687L13.375 12l2.9 2.9q.275.275.275.7t-.275.7q-.3.3-.712.3t-.688-.3z" /></svg>
        </div>
        <nuxt-img v-if="blok.media.filename" v-element-size="onResize" class="z-0 w-full rounded-md" :src="blok.media.filename" loading="lazy" sizes="100vw sm:50vw md:400px" alt="Popup Background" />
        <NuxtLink v-if="blok?.actions?.length || blok.title || blok" target="_blank" :to="blok.link.cached_url" class="absolute inset-0 z-1 flex flex-col items-center justify-center p-6 px-4 md:px-22">
          <header class="mb-3 hidden text-xl text-white font-bold md:block lg:text-3xl md:text-2xl">
            {{ blok.title }}
          </header>
          <div v-if="blok" class="article popup-content hidden text-center text-white md:block [&>*]:!text-white">
            <RichTextRenderer :document="blok.content" />
          </div>
          <ul v-if="blok?.actions?.length" class="mt-custom-md flex flex-wrap gap-4 p-0">
            <li v-for="action in blok.actions" :key="action.uuid" class="mb-0">
              <StoryblokComponent :blok="action" />
            </li>
          </ul>
        </NuxtLink>
      </div>
      <div :class="showConsentAlert && !blok.disable ? 'opacity-100' : 'opacity-0'" class="absolute inset-0 h-full w-full bg-primary/30 backdrop-blur-sm transition-all -z-1" @click="acceptConsent" />
    </div>
  </transition>
</template>

<style scoped>
.popup .article p, .popup .article span, .popup .article ul li {
  @apply !text-white;
}
</style>
